/**
 * AwarenessStrip (W4.3 PHASE 1) — thin, collapsible live-presence strip.
 *
 * Collapsed (default): one line — health dot (green/amber/red) +
 *   "Jarvis: <headline>" + age of the most recent autonomous action.
 * Expanded: the recent_actions list (one line each: source · summary · age).
 *
 * Polls /api/awareness every 25s with backoff (see awareness-store). Hides
 * itself entirely if the endpoint 404s, so the UI ships safely before the API.
 * Works on both Fold screens: single-line collapsed fits the ~374px cover
 * screen; the expanded list scrolls with -webkit-overflow-scrolling + min-height:0.
 */

import { useEffect } from 'preact/hooks';
import { computed } from '@preact/signals';
import {
  awareness,
  endpointAbsent,
  collapsed,
  toggleCollapsed,
  startAwarenessPolling,
} from './awareness-store';
import styles from './AwarenessStrip.module.css';

/** BannerSlot coordination (P1a): mirrors this banner's own visibility rule.
 *  BannerSlot calls startAwarenessPolling() itself on mount, since this strip
 *  is no longer mounted (and thus can't start polling) until data exists. */
export const awarenessStripWantsToShow = computed(
  () => !endpointAbsent.value && awareness.value !== null,
);

function dotClass(dot: string | undefined): string {
  switch (dot) {
    case 'green': return styles.dotGreen;
    case 'red': return styles.dotRed;
    default: return styles.dotAmber;
  }
}

export function AwarenessStrip() {
  // Begin polling once, after mount.
  useEffect(() => {
    startAwarenessPolling();
  }, []);

  // Hide entirely if the endpoint isn't deployed, or before the first payload.
  if (endpointAbsent.value) return null;
  const data = awareness.value;
  if (!data) return null;

  const headline = data.jarvis_state?.headline || 'standing by';
  const actions = data.recent_actions || [];
  const lastActionAge = actions.length > 0 ? actions[0].age : null;
  const isOpen = !collapsed.value;

  return (
    <div class={styles.strip} data-open={isOpen ? 'true' : 'false'}>
      {/* Collapsed summary row — always visible, toggles expansion */}
      <button
        class={styles.summary}
        onClick={toggleCollapsed}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse Jarvis awareness' : 'Expand Jarvis awareness'}
      >
        <span class={`${styles.dot} ${dotClass(data.health_dot)}`} aria-hidden="true" />
        <span class={styles.headline}>
          <span class={styles.label}>Jarvis:</span> {headline}
        </span>
        {lastActionAge && <span class={styles.age}>{lastActionAge}</span>}
        <svg
          class={`${styles.chevron} ${isOpen ? styles.chevronUp : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Expanded action list */}
      {isOpen && (
        <div class={styles.actions}>
          {actions.length === 0 ? (
            <div class={styles.empty}>No autonomous actions in the last 2 hours</div>
          ) : (
            actions.map((a, i) => (
              <div class={styles.action} key={`${a.ts}-${i}`}>
                <span class={styles.actionSource}>{a.source}</span>
                <span class={styles.actionSummary}>{a.summary || a.type}</span>
                <span class={styles.actionAge}>{a.age}</span>
              </div>
            ))
          )}
          {data.system_health && !data.system_health.stale &&
            (data.system_health.failing_count ?? 0) > 0 && (
            <div class={styles.healthNote}>
              {data.system_health.failing_count} health check
              {data.system_health.failing_count === 1 ? '' : 's'} failing
            </div>
          )}
        </div>
      )}
    </div>
  );
}
